import * as React from 'react';
import SearchField from '../components/SearchField';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment-timezone';
import useActivation from '@/src/Activations/components/hooks/useActivation';
import useShare from '@/src/Share/components/hooks/useShare';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import ShareJourneySection from '../components/ShareJourneySection';

/*
 * Insights v2 — the classic stats tab, de-bridged: sessions ledger
 * (thumbnails, recipient search, selection-merge) + shares/media by
 * day with inline bars. Manage → and Create session still land in
 * classic (the Session Manager is its own later slice).
 */

const PAGE = 30;

export default function InsightsV2() {
    const navigate = useNavigate();
    const { activationId } = useParams();
    const id = activationId ? +activationId : 0;
    const { getActivationSessions, getActivationPhotos } = useActivation();
    const { getShareStatsForActivation, mergeSessions } = useShare();
    const authenticatedApi = useAuthenticatedApi();

    const [sessions, setSessions] = useState<any[]>([]);
    const [thumbs, setThumbs] = useState<Record<string, string[]>>({});
    const [shareStats, setShareStats] = useState<any[]>([]);
    const [mediaStats, setMediaStats] = useState<any[]>([]);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<string[]>([]);
    const [limit, setLimit] = useState(PAGE);
    const [isLoading, setIsLoading] = useState(true);
    const [isMerging, setIsMerging] = useState(false);

    const load = async () => {
        const [raw, ss, ms] = await Promise.all([
            getActivationSessions(id).catch(() => []),
            getShareStatsForActivation(id).catch(() => []),
            authenticatedApi
                .get(`/activations/${id}/mediaStats`)
                .then((r) => r.data)
                .catch(() => []),
        ]);
        /* one row per (session, share) — collapse to one per session,
           sent = any share sent, destination = first present. Fall back
           to the row id so sessions WITHOUT a photoSessionId each
           survive (they used to all merge into one — the Insights
           undercount from the IA audit). */
        const byId = new Map<string, any>();
        (raw ?? []).forEach((s: any) => {
            const k = s.photoSessionId ?? `row-${s.id}`;
            const prev = byId.get(k);
            if (!prev) byId.set(k, { ...s });
            else {
                prev.sent = prev.sent || s.sent;
                prev.destination = prev.destination || s.destination;
            }
        });
        setSessions(
            Array.from(byId.values()).sort(
                (a, b) =>
                    moment(b.createdAt ?? b.date).valueOf() -
                    moment(a.createdAt ?? a.date).valueOf(),
            ),
        );
        setShareStats(ss ?? []);
        setMediaStats(ms ?? []);
        setSelected([]);
        setIsLoading(false);
    };

    useEffect(() => {
        load();
        /* thumbnails: one media fetch, grouped by session */
        getActivationPhotos(id, [])
            .then((rows: any[]) => {
                const map: Record<string, string[]> = {};
                (rows ?? []).forEach((m) => {
                    const k = m.photoSessionId;
                    const uri = m.thumbnailTinyUri ?? m.thumbnailUri;
                    if (!k || !uri) return;
                    (map[k] = map[k] ?? []).length < 3 && map[k].push(uri);
                });
                setThumbs(map);
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return sessions.filter(
            (s) =>
                !q || `${s.destination ?? ''}`.toLowerCase().includes(q),
        );
    }, [sessions, search]);

    const sharedCount = sessions.filter((s) => s.destination).length;

    const toggle = (k: string) =>
        setSelected((sel) =>
            sel.includes(k) ? sel.filter((x) => x !== k) : [...sel, k],
        );

    const merge = async () => {
        if (selected.length < 2 || isMerging) return;
        if (
            !window.confirm(
                `Merge ${selected.length} sessions? Photos and shares combine under the first selected session.`,
            )
        )
            return;
        setIsMerging(true);
        await mergeSessions(selected[0], selected.slice(1)).catch(() => null);
        await load();
        setIsMerging(false);
    };

    const maxShare = Math.max(
        1,
        ...shareStats.map((r: any) => Number(r.total) || 0),
    );
    const maxMedia = Math.max(
        1,
        ...mediaStats.map((r: any) => Number(r.total) || 0),
    );

    const bar = (v: number, max: number) => (
        <span
            className="mr-1.5 inline-block h-1 rounded-full bg-brand opacity-75 align-middle"
            style={{ width: `${Math.max(3, (v / max) * 44)}px` }}
        />
    );

    const th = 'py-1 pl-3 text-right font-mono text-[9px] font-medium uppercase tracking-[0.1em] text-faint';
    const td = 'border-t border-line py-[7px] pl-3 text-right tabular-nums text-muted-foreground';

    if (isLoading) {
        return (
            <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
                {[0, 1, 2].map((i) => (
                    <div
                        key={i}
                        className="mb-4 h-12 animate-pulse rounded-[12px] bg-secondary"
                    />
                ))}
            </main>
        );
    }

    return (
        <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-[18px]">Insights</div>

            {/* sessions */}
            <section className="mb-10">
                <div className="mb-1.5 flex items-center gap-2.5">
                    <span className="text-[13px] text-muted-foreground">
                        <b className="font-semibold text-foreground">
                            {sessions.length} session
                            {sessions.length === 1 ? '' : 's'}
                        </b>
                        {' · '}
                        {sharedCount} shared
                    </span>
                    <SearchField
                        className="ml-auto w-[170px]"
                        placeholder="Search recipient…"
                        value={search}
                        onChange={(value) => {
                            setSearch(value);
                            setLimit(PAGE);
                        }}
                    />
                    <button
                        type="button"
                        onClick={() =>
                            navigate(
                                `/v2/activations/${id}/insights/session/new`,
                            )
                        }
                        className="cursor-pointer whitespace-nowrap rounded-full border-0 bg-foreground px-[15px] py-2 font-[inherit] text-[12.5px] font-semibold text-background"
                    >
                        Create session
                    </button>
                </div>

                {filtered.length === 0 && (
                    <p className="py-3 text-[13px] text-muted-foreground">
                        No sessions{search ? ' match' : ' yet'}.
                    </p>
                )}
                {filtered.slice(0, limit).map((s, i) => {
                    const k = s.photoSessionId;
                    const sel = selected.includes(k);
                    const t = thumbs[k] ?? [];
                    return (
                        <div
                            key={k ?? i}
                            className={
                                'flex items-center gap-3 py-2.5 text-[12.5px] ' +
                                (i > 0 ? 'border-t border-line ' : '') +
                                (sel ? 'bg-sunk' : '')
                            }
                        >
                            <button
                                type="button"
                                onClick={() => toggle(k)}
                                className={
                                    'h-4 w-4 flex-none cursor-pointer rounded-[5px] border-[1.5px] p-0 text-[10px] leading-none ' +
                                    (sel
                                        ? 'border-brand bg-brand text-brand-foreground'
                                        : 'border-border bg-transparent text-transparent')
                                }
                            >
                                ✓
                            </button>
                            <span className="flex flex-none">
                                {t.map((uri, ti) => (
                                    <img
                                        key={ti}
                                        src={uri}
                                        alt=""
                                        loading="lazy"
                                        className={
                                            'h-[26px] w-[26px] rounded-[6px] border-[1.5px] border-background object-cover ' +
                                            (ti > 0 ? '-ml-2' : '')
                                        }
                                    />
                                ))}
                                {t.length === 0 && (
                                    <span className="h-[26px] w-[26px] rounded-[6px] bg-secondary" />
                                )}
                            </span>
                            <span className="hidden w-[105px] flex-none text-muted-foreground sm:block">
                                {moment(s.createdAt ?? s.date).format(
                                    'MMM D · h:mm A',
                                )}
                            </span>
                            <span className="w-[70px] flex-none tabular-nums">
                                <b className="font-semibold">
                                    {s.total ?? 0}
                                </b>{' '}
                                photo{Number(s.total) === 1 ? '' : 's'}
                            </span>
                            <span className="hidden min-w-0 flex-1 truncate text-muted-foreground sm:block">
                                {s.destination || '—'}
                            </span>
                            <span className="min-w-0 flex-1 sm:hidden" />
                            <span
                                className={
                                    'w-[56px] flex-none text-[11px] font-semibold ' +
                                    (s.destination
                                        ? s.sent
                                            ? 'text-good'
                                            : 'text-attention'
                                        : 'text-faint')
                                }
                            >
                                {s.destination
                                    ? s.sent
                                        ? 'sent'
                                        : 'unsent'
                                    : 'no share'}
                            </span>
                            <button
                                type="button"
                                onClick={() =>
                                    navigate(
                                        `/v2/activations/${id}/insights/session/${k}`,
                                    )
                                }
                                className="flex-none cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-brand-ink"
                            >
                                Manage →
                            </button>
                        </div>
                    );
                })}
                {filtered.length > limit && (
                    <div className="flex items-center justify-between border-t border-line py-3">
                        <span className="text-[12px] text-faint">
                            {limit} of {filtered.length}
                        </span>
                        <button
                            type="button"
                            onClick={() => setLimit((l) => l + PAGE)}
                            className="ps-pill cursor-pointer px-4 font-[inherit] text-[12.5px] font-semibold text-foreground"
                        >
                            Show more
                        </button>
                    </div>
                )}
            </section>

            {/* share journey — link opens + on-gallery engagement */}
            <ShareJourneySection activationId={id} />

            {/* daily tables */}
            <section className="mb-10">
                <div className="grid gap-0 md:grid-cols-2">
                    <div className="md:pr-9">
                        <span className="ps-label">Shares by day</span>
                        {shareStats.length === 0 ? (
                            <p className="py-3 text-[13px] text-muted-foreground">
                                No shares yet.
                            </p>
                        ) : (
                            <table className="mt-2 w-full border-collapse text-[12px]">
                                <thead>
                                    <tr>
                                        <th className={th + ' pl-0 text-left'}>
                                            Day
                                        </th>
                                        <th className={th}>Total</th>
                                        <th className={th}>Email</th>
                                        <th className={th}>SMS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {shareStats.map((r: any, i: number) => (
                                        <tr key={i}>
                                            <td className={td + ' pl-0 text-left font-semibold text-foreground'}>
                                                {moment(
                                                    r.date ?? r.shareDate,
                                                ).format('MMM D')}
                                            </td>
                                            <td className={td + ' font-semibold text-foreground'}>
                                                {bar(
                                                    Number(r.total) || 0,
                                                    maxShare,
                                                )}
                                                {r.total}
                                            </td>
                                            <td className={td}>
                                                {r.emailCount ?? r.email ?? 0}
                                            </td>
                                            <td className={td}>
                                                {r.textCount ?? r.text ?? 0}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <div className="mt-9 md:mt-0 md:border-l md:border-line md:pl-9">
                        <span className="ps-label">Media by day</span>
                        {mediaStats.length === 0 ? (
                            <p className="py-3 text-[13px] text-muted-foreground">
                                No media yet.
                            </p>
                        ) : (
                            <table className="mt-2 w-full border-collapse text-[12px]">
                                <thead>
                                    <tr>
                                        <th className={th + ' pl-0 text-left'}>
                                            Day
                                        </th>
                                        <th className={th}>Total</th>
                                        <th className={th}>Photos</th>
                                        <th className={th}>Video</th>
                                        <th className={th}>Prints</th>
                                        <th className={th}>Other</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {mediaStats.map((r: any, i: number) => (
                                        <tr key={i}>
                                            <td className={td + ' pl-0 text-left font-semibold text-foreground'}>
                                                {moment(
                                                    r.uploadDate,
                                                    'MM/DD/YYYY',
                                                ).format('MMM D')}
                                            </td>
                                            <td className={td + ' font-semibold text-foreground'}>
                                                {bar(
                                                    Number(r.total) || 0,
                                                    maxMedia,
                                                )}
                                                {r.total}
                                            </td>
                                            <td className={td}>{r.photo ?? 0}</td>
                                            <td className={td}>{r.video ?? 0}</td>
                                            <td className={td}>{r.prints ?? 0}</td>
                                            <td className={td}>
                                                {(Number(r.original) || 0) +
                                                    (Number(r.edited) || 0) +
                                                    (Number(r.other) || 0)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </section>

            {/* merge bar */}
            {selected.length >= 2 && (
                <div className="fixed bottom-[88px] left-1/2 z-30 flex -translate-x-1/2 items-center gap-3.5 rounded-full border border-border bg-background px-[18px] py-[9px] text-[12.5px] shadow-[0_8px_28px_-6px_rgba(20,23,29,0.25)]">
                    <b className="font-semibold">
                        {selected.length} sessions selected
                    </b>
                    <button
                        type="button"
                        disabled={isMerging}
                        onClick={merge}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-brand-ink"
                    >
                        {isMerging ? 'Merging…' : 'Merge into one'}
                    </button>
                    <span className="text-[11.5px] text-faint">
                        photos + shares combine under the first selected
                    </span>
                    <button
                        type="button"
                        onClick={() => setSelected([])}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-faint"
                    >
                        Clear
                    </button>
                </div>
            )}
        </main>
    );
}
